// src/pages/settings/components/NotificationSettings.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  FormGroup,
  FormControlLabel,
  Switch,
  Button,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  useTheme,
  alpha,
  Slider,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  NotificationsActive as NotificationsActiveIcon,
  ContentPaste as ContentIcon,
  Campaign as CampaignIcon,
  Assessment as ReportIcon,
  Receipt as InvoiceIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  VolumeUp as VolumeIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';

// Import stores
import useSettingsStore from '../../../stores/settingsStore';
import useNotificationStore from '../../../stores/notificationStore';
import notificationSoundService from '../../../services/notificationSoundService';

const NotificationSettings = () => {
  const theme = useTheme();
  const { settings, updateNestedSetting } = useSettingsStore();
  const { showSuccess } = useNotificationStore();

  // Get notification settings from store or initialize defaults
  const notificationDefaults = {
    email: {
      contentApproval: true,
      campaigns: true,
      reports: true,
      billing: true,
      systemUpdates: false,
    },
    sms: {
      contentApproval: false,
      campaigns: false,
      reports: false,
      billing: false,
      systemUpdates: false,
    },
    inApp: {
      contentApproval: true,
      campaigns: true,
      reports: true,
      billing: true,
      systemUpdates: true,
    },
    browser: true,
    sound: true,
    soundVolume: notificationSoundService.getVolume(),
    soundType: 'default',
  };

  // Get notification settings from store or initialize with defaults
  const [notificationSettings, setNotificationSettings] = useState(
    settings.notifications || notificationDefaults
  );

  // Initialize sound service settings on mount
  useEffect(() => {
    if (notificationSettings.sound !== undefined) {
      notificationSoundService.setEnabled(notificationSettings.sound);
    }
    if (notificationSettings.soundVolume !== undefined) {
      notificationSoundService.setVolume(notificationSettings.soundVolume);
    }
  }, []);

  // Handle toggle changes for top-level switches
  const handleToggle = (key) => (event) => {
    const newValue = event.target.checked;
    setNotificationSettings((prev) => ({
      ...prev,
      [key]: newValue,
    }));

    // Update sound service if sound setting changed
    if (key === 'sound') {
      notificationSoundService.setEnabled(newValue);
    }
  };

  // Handle volume change
  const handleVolumeChange = (event, newValue) => {
    setNotificationSettings((prev) => ({
      ...prev,
      soundVolume: newValue,
    }));
    notificationSoundService.setVolume(newValue);
  };

  // Test notification sound
  const handleTestSound = () => {
    notificationSoundService.testSound(notificationSettings.soundType || 'default');
  };

  // Handle sound type change
  const handleSoundTypeChange = (event) => {
    setNotificationSettings((prev) => ({
      ...prev,
      soundType: event.target.value,
    }));
  };

  // Handle toggle changes for nested notification types
  const handleNestedToggle = (parentKey, childKey) => (event) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: event.target.checked,
      },
    }));
  };

  // Save notification settings
  const handleSaveSettings = async () => {
    try {
      // Update all notification settings
      await updateNestedSetting('notifications', '', notificationSettings);
      showSuccess('Notification settings saved successfully');
    } catch (error) {
      console.log('Failed to save notification settings:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Notification Settings</Typography>
        <Button 
          variant="contained" 
           
          startIcon={<SaveIcon />}
          onClick={handleSaveSettings}
        >
          Save Changes
        </Button>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      <Grid container spacing={3}>
        {/* General Notification Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <NotificationsIcon sx={{ mr: 1 }} />
              General Notification Settings
            </Typography>
            
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.browser || false}
                    onChange={handleToggle('browser')}
                  />
                }
                label="Browser Notifications"
              />
              <Typography variant="caption" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
                Allow desktop notifications when the app is open
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.sound || false}
                    onChange={handleToggle('sound')}
                  />
                }
                label="Notification Sounds"
              />
              <Typography variant="caption" color="textSecondary" sx={{ ml: 4 }}>
                Play a sound when you receive a notification
              </Typography>

              {/* Sound Settings - only show when sound is enabled */}
              {notificationSettings.sound && (
                <Box sx={{ ml: 4, mt: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" gutterBottom>
                        <VolumeIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                        Volume
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Slider
                          value={notificationSettings.soundVolume || 0.5}
                          onChange={handleVolumeChange}
                          min={0}
                          max={1}
                          step={0.1}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Sound Type</InputLabel>
                        <Select
                          value={notificationSettings.soundType || 'default'}
                          onChange={handleSoundTypeChange}
                          label="Sound Type"
                        >
                          <MenuItem value="default">Default</MenuItem>
                          <MenuItem value="success">Success</MenuItem>
                          <MenuItem value="warning">Warning</MenuItem>
                          <MenuItem value="error">Error</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleTestSound}
                        startIcon={<PlayIcon />}
                        fullWidth
                      >
                        Test
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </FormGroup>
          </Paper>
        </Grid>
        
        {/* Email Notifications */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">Email Notifications</Typography>
            </Box>
            
            <List disablePadding>
              <ListItem disableGutters>
                <ListItemText 
                  primary="Content Approvals" 
                  secondary="Get emails when content requires your approval"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.email?.contentApproval || false}
                    onChange={handleNestedToggle('email', 'contentApproval')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="Campaign Updates" 
                  secondary="Get emails about campaign changes"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.email?.campaigns || false}
                    onChange={handleNestedToggle('email', 'campaigns')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="Reports & Analytics" 
                  secondary="Regular performance reports via email"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.email?.reports || false}
                    onChange={handleNestedToggle('email', 'reports')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="Billing & Invoices" 
                  secondary="Receive invoices and payment reminders"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.email?.billing || false}
                    onChange={handleNestedToggle('email', 'billing')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="System Updates" 
                  secondary="News and feature announcements"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.email?.systemUpdates || false}
                    onChange={handleNestedToggle('email', 'systemUpdates')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>
        </Grid>
        
        {/* SMS Notifications */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SmsIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">SMS Notifications</Typography>
            </Box>
            
            <List disablePadding>
              <ListItem disableGutters>
                <ListItemText 
                  primary="Content Approvals" 
                  secondary="Get SMS when content requires urgent approval"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.sms?.contentApproval || false}
                    onChange={handleNestedToggle('sms', 'contentApproval')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="Campaign Updates" 
                  secondary="Critical campaign alerts via SMS"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.sms?.campaigns || false}
                    onChange={handleNestedToggle('sms', 'campaigns')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="Reports & Analytics" 
                  secondary="Performance highlights via SMS"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.sms?.reports || false}
                    onChange={handleNestedToggle('sms', 'reports')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="Billing & Invoices" 
                  secondary="Payment due reminders via SMS"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.sms?.billing || false}
                    onChange={handleNestedToggle('sms', 'billing')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="System Updates" 
                  secondary="Critical system alerts via SMS"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.sms?.systemUpdates || false}
                    onChange={handleNestedToggle('sms', 'systemUpdates')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
            
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Chip 
                label="SMS charges may apply" 
                color="warning" 
                size="small" 
                variant="outlined" 
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* In-App Notifications */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <NotificationsActiveIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">In-App Notifications</Typography>
            </Box>
            
            <List disablePadding>
              <ListItem disableGutters>
                <ListItemText 
                  primary="Content Approvals" 
                  secondary="Get notified when content requires your approval"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.inApp?.contentApproval || false}
                    onChange={handleNestedToggle('inApp', 'contentApproval')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="Campaign Updates" 
                  secondary="Updates about campaign progress and changes"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.inApp?.campaigns || false}
                    onChange={handleNestedToggle('inApp', 'campaigns')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="Reports & Analytics" 
                  secondary="Notifications when reports are ready"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.inApp?.reports || false}
                    onChange={handleNestedToggle('inApp', 'reports')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="Billing & Invoices" 
                  secondary="Notifications about invoices and payments"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.inApp?.billing || false}
                    onChange={handleNestedToggle('inApp', 'billing')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              
              <ListItem disableGutters>
                <ListItemText 
                  primary="System Updates" 
                  secondary="News and feature announcements"
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={notificationSettings.inApp?.systemUpdates || false}
                    onChange={handleNestedToggle('inApp', 'systemUpdates')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NotificationSettings;